import type {
  PortalWebsiteBreakdownRow,
  PortalWebsiteKeyEventRow,
  PortalWebsitePageRow,
  PortalWebsiteReport,
  PortalWebsiteStudioRow,
} from '../../../../../types/portalWebsite';
import type { PortalReportLabels } from '../../../../../types/portalReport';
import {
  KEY_EVENTS_NOTE,
  PLACES_NOTE,
  SITE_VS_STUDIOS,
  STUDIO_SESSION_MEANING,
  newUsersNote,
  notKyckManagedNote,
  websiteEyebrow,
} from '../../../../../lib/ga4/websiteCopy';
import { Band, Figure, SectionHeading, SwitchGroup } from './controls';
import { DASH, durationText, numberFull } from './format';
import { ReportChartCard } from './ReportChartCard';
import { SortableTable } from './ReportTables';

// CLIENT PORTAL — the Website Analytics (GA4) page. Brief:
// _architect/briefs/2026-09-27-ga4-website-analytics-page.md.
//
// ⚠️⚠️ THIS COMPONENT FILTERS NOTHING. What a studio login may see is decided
// SERVER-SIDE before anything is read (src/lib/ga4/audience.ts) — a studio
// payload simply contains no other studio. Do not add a client-side filter here
// "for safety": a filter after the read is the shape the audience rule exists
// to rule out, and one here would make the payload the thing that leaks.
//
// ⚠️ NO CONVERSIONS: no rate, no total, no cost per. The key events are a list.
// ⚠️ NO STUDIO AS A PERCENTAGE OF THE SITE, anywhere, at either scope.
// ⚠️ BOUNCE RATE AND ENGAGED SESSIONS ARE ONE NUMBER INVERTED, so no panel shows
// both: bounce is the headline (and a chart chip and a comparison column);
// engaged sessions appear only in "Where they came from".

/** The metric chips carry no brand vocabulary; the chart card still wants a registry. */
const NO_LABELS = {} as PortalReportLabels;

const pct = (v: number | null): string | null => (v === null ? null : `${(v * 100).toFixed(1)}%`);
const count = (v: number | null): string | null => (v === null ? null : numberFull(v));
const secs = (v: number | null): string | null => (v === null ? null : durationText(v));

export function WebsitePage({
  report,
  onStudioChange,
}: {
  report: PortalWebsiteReport;
  today: string;
  /** Absent → no picker (the staff preview can still pass one). */
  onStudioChange?: (studio: string | null) => void;
}) {
  const selected = report.studios.find((s) => s.key === report.selectedStudio) ?? null;
  const h = report.headline;

  return (
    <>
      <Band variant="hero">
        <div className="rpt-hero">
          <div>
            <span className="rpt-mono">{websiteEyebrow(selected?.label ?? null)}</span>
            <p className="rpt-hero-figure">{count(h.sessions) ?? DASH}</p>
            <p className="rpt-sub rpt-muted">Visits to the website in this period.</p>
          </div>
          <div className="rpt-stat-grid">
            <p className="rpt-stat">
              <Figure value={count(h.newUsers)} className="rpt-stat-figure" />
              <span className="rpt-mono rpt-stat-label">New users</span>
            </p>
            <p className="rpt-stat">
              <Figure value={pct(h.bounceRate)} className="rpt-stat-figure" />
              <span className="rpt-mono rpt-stat-label">Bounce rate</span>
            </p>
            <p className="rpt-stat">
              <Figure value={secs(h.avgSessionDurationSec)} className="rpt-stat-figure" />
              <span className="rpt-mono rpt-stat-label">Avg session duration</span>
            </p>
          </div>
        </div>
      </Band>

      <Band>
        {onStudioChange && report.studios.length > 1 && (
          <div className="rpt-table-controls" style={{ marginBottom: 12 }}>
            <SwitchGroup
              ariaLabel="Studio"
              value={report.selectedStudio ?? '__all'}
              onChange={(v) => onStudioChange(v === '__all' ? null : v)}
              options={[
                // ⚠️ "All studios" exists only for the BRAND — a studio login has
                // no site-wide view to return to.
                ...(report.audience === 'brand' ? [{ value: '__all', label: 'All studios' }] : []),
                ...report.studios.map((s) => ({ value: s.key, label: s.label })),
              ]}
            />
          </div>
        )}
        <div className="rpt-note-row">
          {report.audience !== 'owned' && <p className="rpt-note rpt-muted">{STUDIO_SESSION_MEANING}</p>}
          {report.audience === 'brand' && <p className="rpt-note rpt-muted">{SITE_VS_STUDIOS}</p>}
          <p className="rpt-note rpt-muted">{newUsersNote(h.newUsersBasis)}</p>
        </div>
      </Band>

      {report.comparison && (
        <Band>
          <SectionHeading
            title="Your studios"
            intro="Each studio's own website figures for the period. Sort any column."
          />
          <StudioComparison rows={report.comparison} selected={report.selectedStudio} />
          {report.comparison.some((r) => !r.kyckManaged) && (
            <p className="rpt-note rpt-muted">{notKyckManagedNote()}</p>
          )}
        </Band>
      )}

      <Band>
        <SectionHeading title="Over time" intro="Choose between past 12 months or reporting period." />
        {report.studioOpenedInWindow && (
          <p className="rpt-note rpt-muted" style={{ marginBottom: 12 }}>
            {selected?.label ?? 'This studio'} opened part-way through the last 12 months, so the chart
            starts when it did rather than showing the months before as zero.
          </p>
        )}
        <ReportChartCard
          title="Website visits"
          source={{ kind: 'website', period: report.periodSeries, months: report.monthlySeries }}
          metrics={['webSessions', 'webNewUsers', 'webBounceRate', 'webAvgDuration']}
          labels={NO_LABELS}
          currency="AUD"
          periodLabel="this period"
        />
      </Band>

      <Band variant="contrast">
        <SectionHeading
          title="Where they came from"
          intro="How visitors reached the website, and how many of those visits were engaged — a visit that lasted over 10 seconds, viewed two pages or did something."
        />
        {/* ⚠️ SOURCE / MEDIUM ONLY. The stored channel report is GA4's key-event
            attribution channel and covers ~2% of sessions — see portalService. */}
        <BreakdownTable caption="Source / medium" rows={report.sources} withEngaged />
      </Band>

      <Band>
        <SectionHeading title="Where they are" intro={PLACES_NOTE} />
        <BreakdownTable caption="Town or city" rows={report.places} detailLabel="Region" />
      </Band>

      <Band>
        <SectionHeading title="How they arrived" />
        <div className="rpt-card-grid rpt-card-grid--2">
          <BreakdownTable caption="Device" rows={report.devices} />
          <BreakdownTable caption="New or returning" rows={report.newVsReturning} />
        </div>
      </Band>

      <Band>
        <SectionHeading title="Top pages" intro="The pages people looked at most in this period." />
        <PagesTable rows={report.topPages} />
      </Band>

      <Band>
        <SectionHeading title="Key events" intro={KEY_EVENTS_NOTE} />
        <KeyEventsTable rows={report.keyEvents} />
      </Band>
    </>
  );
}

function StudioComparison({ rows, selected }: { rows: PortalWebsiteStudioRow[]; selected: string | null }) {
  return (
    <SortableTable
      caption="Each studio's website figures"
      rows={rows}
      rowKey={(r) => r.key}
      initialSort="sessions"
      rowClassName={(r) => (r.key === selected ? 'rpt-row-selected' : undefined)}
      columns={[
        {
          key: 'label',
          label: 'Studio',
          align: 'left',
          render: (r) => (
            <>
              {r.label}
              {!r.kyckManaged && <span className="rpt-muted"> · not managed by Kyck</span>}
              {r.key === selected && <span className="rpt-muted"> · selected</span>}
            </>
          ),
        },
        { key: 'sessions', label: 'Sessions', render: (r) => count(r.sessions) ?? DASH },
        { key: 'bounceRate', label: 'Bounce rate', render: (r) => pct(r.bounceRate) ?? DASH },
        { key: 'avgSessionDurationSec', label: 'Avg duration', render: (r) => secs(r.avgSessionDurationSec) ?? DASH },
        { key: 'pageViews', label: 'Page views', render: (r) => count(r.pageViews) ?? DASH },
      ]}
    />
  );
}

function BreakdownTable({
  caption,
  rows,
  withEngaged = false,
  detailLabel,
}: {
  caption: string;
  rows: PortalWebsiteBreakdownRow[];
  /** ⚠️ Only "Where they came from" — see the header on bounce vs engaged. */
  withEngaged?: boolean;
  detailLabel?: string;
}) {
  if (rows.length === 0) return <p className="rpt-note rpt-muted">Nothing recorded in this period.</p>;
  return (
    <SortableTable
      caption={caption}
      rows={rows}
      rowKey={(r, i) => `${r.label}|${r.detail ?? ''}|${i}`}
      initialSort="sessions"
      columns={[
        { key: 'label', label: caption, align: 'left', render: (r) => r.label },
        ...(detailLabel
          ? [{ key: 'detail' as const, label: detailLabel, align: 'left' as const, render: (r: PortalWebsiteBreakdownRow) => r.detail ?? DASH }]
          : []),
        { key: 'sessions', label: 'Sessions', render: (r) => count(r.sessions) ?? DASH },
        ...(withEngaged
          ? [{ key: 'engagedSessions' as const, label: 'Engaged', render: (r: PortalWebsiteBreakdownRow) => count(r.engagedSessions) ?? DASH }]
          : []),
      ]}
    />
  );
}

function PagesTable({ rows }: { rows: PortalWebsitePageRow[] }) {
  if (rows.length === 0) return <p className="rpt-note rpt-muted">Nothing recorded in this period.</p>;
  return (
    <SortableTable
      caption="Top pages"
      rows={rows}
      rowKey={(r) => r.page}
      initialSort="pageViews"
      columns={[
        { key: 'page', label: 'Page', align: 'left', render: (r) => r.page },
        { key: 'pageViews', label: 'Page views', render: (r) => count(r.pageViews) ?? DASH },
      ]}
    />
  );
}

function KeyEventsTable({ rows }: { rows: PortalWebsiteKeyEventRow[] }) {
  if (rows.length === 0) return <p className="rpt-note rpt-muted">No key events recorded in this period.</p>;
  return (
    <SortableTable
      caption="Key events"
      rows={rows}
      rowKey={(r) => r.eventName}
      initialSort="count"
      columns={[
        { key: 'eventName', label: 'Event', align: 'left', render: (r) => r.eventName },
        { key: 'count', label: 'Times recorded', render: (r) => count(r.count) ?? DASH },
      ]}
    />
  );
}
